import { useEffect } from "react";
import { Duration } from "luxon";
import { plural, t } from "@lingui/core/macro";
import { createActionToastQueue } from "@fidelix/fx-miranda";
import { HistoryResolution } from "../graphics.queries";

// Own queue so the banner only shows in the history mode toast region
export const historyToasts = createActionToastQueue();

function formatResolution(resolution: string | null) {
  if (resolution === null) {
    return t`Raw data`;
  }
  const minutes = Math.round(Duration.fromISO(resolution).as("minutes"));
  return plural(minutes, { one: "# minute", other: "# minutes" });
}

function getHistoryResolutionText({
  analog,
  digital,
}: HistoryResolution): string | undefined {
  const analogText = analog !== undefined && formatResolution(analog);
  const digitalText = digital !== undefined && formatResolution(digital);

  if (analogText && digitalText) {
    return t`History mode resolution: Analog points ${analogText}, Digital points ${digitalText}`;
  }
  if (analogText) {
    return t`History mode resolution: Analog points ${analogText}`;
  }
  if (digitalText) {
    return t`History mode resolution: Digital points ${digitalText}`;
  }
  return undefined;
}

// Shows the resolution info toast and re-shows it whenever the resolution changes
export function useHistoryResolutionToast(resolution?: HistoryResolution) {
  const text = resolution ? getHistoryResolutionText(resolution) : undefined;

  useEffect(() => {
    if (!text) {
      return;
    }
    historyToasts.clear();
    historyToasts.show({ title: text, variant: "dark" }, { timeout: 0 });
  }, [text]);

  useEffect(() => historyToasts.clear, []);
}
